import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusinessId } from '../hooks/useBusinessId';
import { useBusiness } from '../contexts/BusinessContext';

interface SubCategory {
  id: string;
  title: string;
  services: Array<{
    service: {
      id: string;
      name: string;
      description?: string;
    };
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }>;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  category_title: string;
  sub_categories?: SubCategory[];
}

export default function OnlineBookingEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supabase = useSupabase();
  const businessId = useBusinessId();
  const { selectedBusiness } = useBusiness();
  const [onlineBookingPortalId, setOnlineBookingPortalId] = useState<string | null>(null);

  const [isEditMode, setIsEditMode] = useState(true);
  const [currentStage, setCurrentStage] = useState<'category' | 'service'>('category');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCategoryForServices, setSelectedCategoryForServices] = useState<string | null>(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryTitle, setNewCategoryTitle] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryTitle, setEditingCategoryTitle] = useState('');

  useEffect(() => {
    if (!supabase || !businessId) return;
    const q = searchParams.get('portal');
    (async () => {
      if (q) {
        setOnlineBookingPortalId(q);
        return;
      }
      const { data } = await supabase
        .from('online_booking_portals')
        .select('id')
        .eq('business_id', businessId)
        .eq('is_default', true)
        .maybeSingle();
      setOnlineBookingPortalId(data?.id ?? null);
    })();
  }, [businessId, searchParams, supabase]);

  const fetchCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }
      
      if (!businessId || !onlineBookingPortalId) {
        throw new Error('Business not loaded');
      }
      
      const { data, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories')
        .eq('business_id', businessId)
        .eq('portal_id', onlineBookingPortalId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Unable to load service categories');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (businessId && onlineBookingPortalId) {
      fetchCategories();
    }
  }, [businessId, onlineBookingPortalId]);

  const handleAddCategory = async () => {
    if (!newCategoryTitle.trim() || !businessId || !onlineBookingPortalId) return;

    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .insert([{
          business_id: businessId,
          portal_id: onlineBookingPortalId,
          category_title: newCategoryTitle.trim()
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setNewCategoryTitle('');
      setIsAddingCategory(false);
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('online_booking_flow')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      setCategories(categories.filter(cat => cat.id !== categoryId));
      if (selectedCategory === categoryId) {
        setSelectedCategory(null);
      }
      toast.success('Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleEditCategory = (categoryId: string, currentTitle: string) => {
    setEditingCategoryId(categoryId);
    setEditingCategoryTitle(currentTitle);
  };

  const handleSaveEditCategory = async () => {
    if (!editingCategoryTitle.trim() || !editingCategoryId) return;

    try {
      const { error } = await supabase
        .from('online_booking_flow')
        .update({ category_title: editingCategoryTitle.trim() })
        .eq('id', editingCategoryId);

      if (error) throw error;

      // Update local state
      setCategories(categories.map(cat => 
        cat.id === editingCategoryId 
          ? { ...cat, category_title: editingCategoryTitle.trim() }
          : cat
      ));

      setEditingCategoryId(null);
      setEditingCategoryTitle('');
      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryTitle('');
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    toast.success('Changes saved successfully!');
  };

  const handlePreview = () => {
    console.log('Toggling edit mode from:', isEditMode, 'to:', !isEditMode);
    const newMode = !isEditMode;
    setIsEditMode(newMode);
    
    // Show feedback to user
    if (newMode) {
      toast.info('Switched to Edit Mode - hover categories to see controls');
    } else {
      toast.info('Switched to Preview Mode - see exactly what customers see');
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const renderCategoriesStage = () => {
    return (
      <div className="w-full space-y-4">
        {isCategoriesLoading ? (
          <div className="text-center text-lg font-medium text-gray-600">Loading categories...</div>
        ) : error ? (
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600 text-base font-medium">{error}</div>
            </div>
          </div>
        ) : categories.length === 0 && !isAddingCategory ? (
          <div className="text-center">
            <div className="text-lg font-medium text-gray-600 mb-4">No service categories available</div>
            {isEditMode && (
              <button
                onClick={() => setIsAddingCategory(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-all duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Category
              </button>
            )}
          </div>
        ) : (
          <>
            {categories.map((category) => (
              <div key={category.id} className="relative group">
                {editingCategoryId === category.id ? (
                  // Edit Mode - Inline Input
                  <div className="w-full py-4 px-6 rounded-lg border-2 border-blue-300 bg-blue-50">
                    <input
                      type="text"
                      value={editingCategoryTitle}
                      onChange={(e) => setEditingCategoryTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEditCategory();
                        } else if (e.key === 'Escape') {
                          handleCancelEditCategory();
                        }
                      }}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveEditCategory}
                        disabled={!editingCategoryTitle.trim()}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditCategory}
                        className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Normal Mode - Category Button
                  <>
                    <button
                      onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                      className={`w-full py-4 px-6 rounded-lg border-2 transition-all duration-200 text-lg font-medium ${
                        selectedCategory === category.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400 bg-white text-gray-900'
                      }`}
                    >
                      {category.category_title}
                    </button>
                    
                    {/* Edit Mode Controls */}
                    {isEditMode && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(category.id, category.category_title);
                          }}
                          className="p-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
                          title="Edit category"
                        >
                          <Edit className="h-3 w-3 text-gray-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this category?')) {
                              handleDeleteCategory(category.id);
                            }
                          }}
                          className="p-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-red-50 hover:border-red-300 transition-colors"
                          title="Delete category"
                        >
                          <Trash2 className="h-3 w-3 text-red-600" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            
            {/* Add New Category Button/Form */}
            {isEditMode && (
              <div className="relative">
                {isAddingCategory ? (
                  <div className="py-4 px-6 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50">
                    <input
                      type="text"
                      value={newCategoryTitle}
                      onChange={(e) => setNewCategoryTitle(e.target.value)}
                      placeholder="Enter category name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddCategory();
                        } else if (e.key === 'Escape') {
                          setIsAddingCategory(false);
                          setNewCategoryTitle('');
                        }
                      }}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleAddCategory}
                        disabled={!newCategoryTitle.trim()}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        Add Category
                      </button>
                      <button
                        onClick={() => {
                          setIsAddingCategory(false);
                          setNewCategoryTitle('');
                        }}
                        className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddingCategory(true)}
                    className="w-full py-4 px-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 text-gray-600 hover:text-blue-600"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Plus className="h-5 w-5" />
                      <span className="text-base font-medium">Add Category</span>
                    </div>
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-center text-red-600 text-base font-medium">{error}</p>
          </div>
        )}
      </div>
    );
  };

  const renderServicesStage = () => {
    const selectedCategoryData = categories.find(cat => cat.id === selectedCategoryForServices);
    
    return (
      <div className="w-full space-y-4">
        {!selectedCategoryForServices ? (
          <div className="text-center py-12">
            <div className="text-lg font-medium text-gray-600 mb-4">
              Select a category from the sidebar to manage its sub-categories and services
            </div>
            <div className="text-sm text-gray-500">
              Choose a category on the left to get started
            </div>
          </div>
        ) : !selectedCategoryData ? (
          <div className="text-center py-12">
            <div className="text-lg font-medium text-gray-600">Loading category data...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Category Header */}
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Managing: {selectedCategoryData.category_title}
              </h2>
              <p className="text-sm text-gray-600">
                Add and organize sub-categories and services for this category
              </p>
            </div>

            {/* Sub-categories List */}
            <div className="space-y-4">
              {selectedCategoryData.sub_categories && selectedCategoryData.sub_categories.length > 0 ? (
                selectedCategoryData.sub_categories.map((subCategory) => (
                  <div key={subCategory.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-medium text-gray-900">{subCategory.title}</h3>
                      {isEditMode && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              // TODO: Implement edit sub-category
                              toast.info('Edit sub-category functionality coming soon');
                            }}
                            className="p-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
                            title="Edit sub-category"
                          >
                            <Edit className="h-3 w-3 text-gray-600" />
                          </button>
                          <button
                            onClick={() => {
                              // TODO: Implement delete sub-category
                              toast.info('Delete sub-category functionality coming soon');
                            }}
                            className="p-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-red-50 hover:border-red-300 transition-colors"
                            title="Delete sub-category"
                          >
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Services in this sub-category */}
                    <div className="space-y-2">
                      {subCategory.services && subCategory.services.length > 0 ? (
                        subCategory.services.map((service, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-md p-3 flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900">{service.service.name}</div>
                              <div className="text-sm text-gray-600">
                                {service.priceOption.name} - ${service.priceOption.price} ({service.priceOption.duration_minutes} min)
                              </div>
                            </div>
                            {isEditMode && (
                              <button
                                onClick={() => {
                                  // TODO: Implement remove service
                                  toast.info('Remove service functionality coming soon');
                                }}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title="Remove service"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          No services assigned to this sub-category
                        </div>
                      )}
                      
                      {/* Add Service Button */}
                      {isEditMode && (
                        <button
                          onClick={() => {
                            // TODO: Implement add service
                            toast.info('Add service functionality coming soon');
                          }}
                          className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors"
                        >
                          + Add Service
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-600 mb-4">No sub-categories created yet</div>
                  {isEditMode && (
                    <button
                      onClick={() => {
                        // TODO: Implement add sub-category
                        toast.info('Add sub-category functionality coming soon');
                      }}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-all duration-200"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Sub-Category
                    </button>
                  )}
                </div>
              )}
              
              {/* Add Sub-Category Button */}
              {isEditMode && selectedCategoryData.sub_categories && selectedCategoryData.sub_categories.length > 0 && (
                <button
                  onClick={() => {
                    // TODO: Implement add sub-category
                    toast.info('Add sub-category functionality coming soon');
                  }}
                  className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">Add Sub-Category</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCategorySidebar = () => {
    return (
      <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Categories</h3>
          <p className="text-xs text-gray-600">Select a category to manage its sub-categories</p>
        </div>
        
        <div className="space-y-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategoryForServices(category.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedCategoryForServices === category.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">{category.category_title}</div>
              <div className="text-xs text-gray-500 mt-1">
                {category.sub_categories?.length || 0} sub-categories
              </div>
            </button>
          ))}
        </div>
        
        {categories.length === 0 && (
          <div className="text-center py-4">
            <div className="text-sm text-gray-500 mb-2">No categories yet</div>
            <button
              onClick={() => setCurrentStage('category')}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Go create categories first
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Editor Controls Header - Only show in edit mode */}
      {isEditMode && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 relative z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Online Booking Editor
                </h1>
                <p className="text-sm text-gray-500">
                  Edit your booking page visually - click elements to modify them
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePreview}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </button>
              
              <button
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-all duration-200"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Booking Page Replica */}
      <div className={`${isEditMode ? '' : 'min-h-screen'} bg-white flex flex-col`}>
        {/* Dark Header - Exact replica */}
        <div className="bg-black text-white px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo/Brand */}
            <div className="flex items-center">
              {selectedBusiness?.booking_settings?.logo_url ? (
                <img 
                  src={selectedBusiness.booking_settings.logo_url} 
                  alt={`${selectedBusiness.name} logo`}
                  className="h-8 w-auto"
                />
              ) : (
                <div className="text-xl font-bold">
                  {selectedBusiness?.name || 'Business Name'}
                </div>
              )}
            </div>
            
            {/* Preview mode controls */}
            {!isEditMode && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePreview}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-700 transition-all duration-200"
                >
                  <EyeOff className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleBack}
                  className="text-white hover:text-gray-300 transition-colors"
                  title="Close editor"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex">
          {/* Category Sidebar - Only show in services stage and edit mode */}
          {currentStage === 'service' && isEditMode && renderCategorySidebar()}
          
          {/* Main Content */}
          <div className={`flex-1 flex flex-col justify-center items-center px-6 py-12 ${
            currentStage === 'service' && isEditMode ? '' : ''
          }`}>
            {/* Page Title */}
            <div className="mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-900">
                {currentStage === 'category' ? 'Select A Category' : 'Choose Your Service'}
              </h1>
            </div>

            {/* Stage Content */}
            <div className="w-full max-w-4xl mx-auto mb-12">
              {currentStage === 'category' ? (
                <div className="max-w-md mx-auto">
                  {renderCategoriesStage()}
                </div>
              ) : (
                renderServicesStage()
              )}
            </div>

            {/* Next Button - Only show in preview mode */}
            {!isEditMode && (
              <div className="w-full max-w-md mx-auto mb-8">
                <button
                  disabled={currentStage === 'category' ? !selectedCategory : false}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 ${
                    (currentStage === 'category' && !selectedCategory)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  NEXT
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Progress Stepper - Bottom */}
        <div className="bg-white border-t border-gray-200 px-6 py-6">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between">
              {/* Step 1 - Location (Completed) */}
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold mb-2">
                  1
                </div>
                <span className="text-xs text-gray-600">Location</span>
              </div>
              
              {/* Connector Line */}
              <div className={`flex-1 h-0.5 mx-2 ${
                currentStage === 'service' ? 'bg-blue-500' : 'bg-blue-500'
              }`}></div>
              
              {/* Step 2 - Category */}
              <button 
                onClick={() => {
                  setCurrentStage('category');
                  if (isEditMode) {
                    toast.info('Switched to Category stage');
                  }
                }}
                className="flex flex-col items-center hover:opacity-80 transition-opacity"
                disabled={!isEditMode}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold mb-2 ${
                  currentStage === 'category' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-green-500 text-white'
                }`}>
                  2
                </div>
                <span className={`text-xs ${
                  currentStage === 'category' 
                    ? 'text-blue-600 font-medium' 
                    : 'text-gray-600'
                }`}>Category</span>
              </button>
              
              {/* Connector Line */}
              <div className={`flex-1 h-0.5 mx-2 ${
                currentStage === 'service' ? 'bg-blue-500' : 'bg-gray-300'
              }`}></div>
              
              {/* Step 3 - Service */}
              <button 
                onClick={() => {
                  setCurrentStage('service');
                  if (isEditMode) {
                    toast.info('Switched to Service stage');
                  }
                }}
                className="flex flex-col items-center hover:opacity-80 transition-opacity"
                disabled={!isEditMode}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold mb-2 ${
                  currentStage === 'service' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-300 text-gray-500'
                }`}>
                  3
                </div>
                <span className={`text-xs ${
                  currentStage === 'service' 
                    ? 'text-blue-600 font-medium' 
                    : 'text-gray-500'
                }`}>Service</span>
              </button>
              
              {/* Connector Line */}
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              
              {/* Step 4 - Date & Time */}
              <div className="flex flex-col items-center opacity-50">
                <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold mb-2">
                  4
                </div>
                <span className="text-xs text-gray-500">Date & Time</span>
              </div>
              
              {/* Connector Line */}
              <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
              
              {/* Step 5 - Info */}
              <div className="flex flex-col items-center opacity-50">
                <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold mb-2">
                  5
                </div>
                <span className="text-xs text-gray-500">Info</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
